import chalk from 'chalk';

/**
 * Directories code migrations must never walk. Without this, patterns like
 * `** /web/** /package.json` match vendored files under `node_modules` — the
 * migration then rewrites (and drops a `.bak` beside) files npm blows away on the
 * next install, and the scan needlessly walks hundreds of files (the historical
 * "584 skipped" noise). bin/obj/dist are build output; .git is repo metadata.
 */
export const MIGRATION_IGNORE_GLOBS = [
  '**/node_modules/**',
  '**/bin/**',
  '**/obj/**',
  '**/dist/**',
  '**/.git/**',
];

/** The subset of upgrade results that decides the final outcome. */
export interface UpgradeFailureState {
  /** True when a `dotnet restore` failed — the .csproj was edited but won't restore. */
  restoreFailed: boolean;
  /** Count of SmartStack package upgrades that errored. */
  nugetFailed: number;
  /** Count of other-package upgrades that errored (--all-packages). */
  otherPkgFailed: number;
  /** Count of required packages that failed to add. */
  requiredPkgFailed: number;
  /** Missing Program.cs bootstrap calls (a warning, not a hard failure). */
  programCsIssues: string[];
}

export interface UpgradeOutcome {
  /** True when a package add or restore failed — the project build may be broken. */
  hasFailures: boolean;
  /** Process exit code: 1 when the upgrade left the project in a broken state. */
  exitCode: number;
  /** Box rendering style for the final summary. */
  boxStyle: 'success' | 'warning' | 'error';
  /** Headline shown at the top of the final summary box. */
  title: string;
}

/**
 * Pure decision: given what actually happened, what should the final summary say and
 * what exit code should the process carry. A failed restore / package add is a HARD
 * failure (red box, exit 1) so `ss upgrade` never reports green over a broken build —
 * critical for CI and worktree-driven runs. A missing Program.cs call is a warning
 * (exit 0): the upgrade succeeded, the user just has wiring to finish.
 */
export function computeUpgradeOutcome(result: UpgradeFailureState): UpgradeOutcome {
  const hasHardFailures =
    result.restoreFailed ||
    result.nugetFailed > 0 ||
    result.otherPkgFailed > 0 ||
    result.requiredPkgFailed > 0;

  if (hasHardFailures) {
    return {
      hasFailures: true,
      exitCode: 1,
      boxStyle: 'error',
      title: chalk.red.bold('Upgrade completed with errors'),
    };
  }

  if (result.programCsIssues.length > 0) {
    return {
      hasFailures: false,
      exitCode: 0,
      boxStyle: 'warning',
      title: chalk.yellow.bold('Upgrade completed — Program.cs needs attention'),
    };
  }

  return {
    hasFailures: false,
    exitCode: 0,
    boxStyle: 'success',
    title: chalk.green.bold('Upgrade completed!'),
  };
}
